import { useEffect, useState } from 'react';

const LANGUAGE_STORAGE_KEY = 'thoughtful-oasis-language';
const LANGUAGE_CHANGE_EVENT = 'thoughtful-oasis-language-change';

type Lang = 'en' | 'nl';

function readStoredLanguage(fallback: Lang = 'en'): Lang {
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === 'en' || stored === 'nl') return stored;
  } catch {
    // localStorage may be unavailable (private mode, etc.)
  }
  return fallback;
}

// `defaultLanguage` sets the fallback used when nothing is stored yet — a
// per-caller override, so a single route (e.g. `/vmo`, which is aimed at Dutch
// civil servants) can render in NL by default without changing the global
// default for the rest of the site. Storage still wins when it holds a value,
// so any explicit EN/NL toggle keeps working exactly as before.
export const useLanguage = (
  defaultLanguage: Lang = 'en',
): [Lang, (lang: Lang) => void] => {
  const [language, setLanguageState] = useState<Lang>(() =>
    readStoredLanguage(defaultLanguage),
  );

  // Sync this hook instance with language changes triggered anywhere on the page
  // (other components calling setLanguage) or in other tabs (storage event).
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onLocalChange = (e: Event) => {
      const detail = (e as CustomEvent<Lang>).detail;
      if (detail === 'en' || detail === 'nl') {
        setLanguageState(detail);
      }
    };

    const onStorageChange = (e: StorageEvent) => {
      if (e.key !== LANGUAGE_STORAGE_KEY) return;
      const next =
        e.newValue === 'en' || e.newValue === 'nl'
          ? (e.newValue as Lang)
          : defaultLanguage;
      setLanguageState(next);
    };

    window.addEventListener(LANGUAGE_CHANGE_EVENT, onLocalChange as EventListener);
    window.addEventListener('storage', onStorageChange);

    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, onLocalChange as EventListener);
      window.removeEventListener('storage', onStorageChange);
    };
  }, [defaultLanguage]);

  const setLanguage = (lang: Lang) => {
    if (typeof window === 'undefined') {
      setLanguageState(lang);
      return;
    }
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    } catch {
      // ignore storage failure
    }
    setLanguageState(lang);
    window.dispatchEvent(new CustomEvent<Lang>(LANGUAGE_CHANGE_EVENT, { detail: lang }));
  };

  return [language, setLanguage];
};
